import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';

interface NeighborhoodStats {
  name: string;
  city: string;
  totalListings: number;
  medianPrice: number;
  priceRange: { min: number; max: number };
  demographics: {
    medianIncome: number;
    avgHomeAge: number;
    ownerOccupied: number;
  };
  schools: Array<{
    name: string;
    type: string;
    rating: number;
  }>;
  amenities: string[];
  recentSales: Array<{
    address: string;
    soldPrice: number;
    soldDate: string;
    daysOnMarket: number;
  }>;
}

export const NeighborhoodLanding: React.FC = () => {
  const { city, neighborhood } = useParams<{ city: string; neighborhood: string }>();
  const [stats, setStats] = useState<NeighborhoodStats | null>(null);
  const [loading, setLoading] = useState(true);
  
  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : '';
  const neighborhoodName = neighborhood ? neighborhood.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';

  useEffect(() => {
    const fetchNeighborhoodData = async () => {
      if (!city || !neighborhood) return;
      
      try {
        const response = await axios.get(`/api/mls-portal/neighborhoods/${city}/${neighborhood}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching neighborhood data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNeighborhoodData();
  }, [city, neighborhood]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Place",
    "name": `${neighborhoodName}, ${cityName}, AZ`,
    "description": `Homes for sale in ${neighborhoodName}, ${cityName}, Arizona. Browse ${stats?.totalListings || ''} properties with median price $${stats?.medianPrice.toLocaleString() || ''}.`,
    "url": `https://chat.realestate/homes-for-sale/${city}/${neighborhood}`,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": cityName,
      "addressRegion": "AZ",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": null, // Would be populated from actual data
      "longitude": null
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!stats) return <div>Neighborhood not found</div>;

  return (
    <>
      <Helmet>
        <title>{neighborhoodName} Homes for Sale in {cityName}, AZ | Chat.RealEstate</title>
        <meta name="description" content={`Search ${stats.totalListings} homes for sale in ${neighborhoodName}, ${cityName}, AZ. Median home price $${stats.medianPrice.toLocaleString()}. View listings, recent sales, and neighborhood information.`} />
        <link rel="canonical" href={`https://chat.realestate/homes-for-sale/${city}/${neighborhood}`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb Navigation */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <nav className="flex items-center space-x-2 text-sm">
              <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
              <span className="text-gray-400">/</span>
              <Link to="/arizona" className="text-blue-600 hover:text-blue-800">Arizona</Link>
              <span className="text-gray-400">/</span>
              <Link to={`/homes-for-sale/${city}`} className="text-blue-600 hover:text-blue-800">{cityName}</Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700">{neighborhoodName}</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 text-white">
          <div className="max-w-7xl mx-auto px-4 py-12">
            <h1 className="text-4xl font-bold mb-4">
              {neighborhoodName} Real Estate
            </h1>
            <p className="text-xl">
              {stats.totalListings} Homes for Sale in {neighborhoodName}, {cityName}, AZ
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Market Overview */}
              <section className="bg-white rounded-lg p-6 shadow mb-8">
                <h2 className="text-2xl font-bold mb-4">{neighborhoodName} Market Overview</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Median Price</div>
                    <div className="text-2xl font-bold">${stats.medianPrice.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Active Listings</div>
                    <div className="text-2xl font-bold">{stats.totalListings}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Price Range</div>
                    <div className="text-lg font-semibold">
                      ${stats.priceRange.min.toLocaleString()} - ${stats.priceRange.max.toLocaleString()}
                    </div>
                  </div>
                </div>
              </section>

              {/* Recent Sales */}
              <section className="bg-white rounded-lg p-6 shadow mb-8">
                <h2 className="text-2xl font-bold mb-4">Recent Sales in {neighborhoodName}</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Address</th>
                        <th className="text-right py-2">Sold Price</th>
                        <th className="text-right py-2">Days on Market</th>
                        <th className="text-right py-2">Sale Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentSales.map((sale, index) => (
                        <tr key={index} className="border-b">
                          <td className="py-3">{sale.address}</td>
                          <td className="text-right font-semibold">${sale.soldPrice.toLocaleString()}</td>
                          <td className="text-right">{sale.daysOnMarket}</td>
                          <td className="text-right text-gray-600">{new Date(sale.soldDate).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Schools */}
              <section className="bg-white rounded-lg p-6 shadow mb-8">
                <h2 className="text-2xl font-bold mb-4">Schools Near {neighborhoodName}</h2>
                <div className="space-y-4">
                  {stats.schools.map((school, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded">
                      <div>
                        <h3 className="font-semibold">{school.name}</h3>
                        <p className="text-sm text-gray-600">{school.type}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-600">{school.rating}/10</div>
                        <div className="text-sm text-gray-600">Rating</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* AI Search CTA */}
              <div className="bg-blue-600 text-white rounded-lg p-8 text-center">
                <h2 className="text-2xl font-bold mb-4">
                  Find Your Dream Home in {neighborhoodName}
                </h2>
                <p className="text-lg mb-6">
                  Use our AI-powered search to find homes that match your specific needs
                </p>
                <Link 
                  to={`/search?subdivision=${neighborhoodName}&city=${cityName}`}
                  className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
                >
                  Search {neighborhoodName} Homes with AI
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              {/* Neighborhood Stats */}
              <div className="bg-white rounded-lg p-6 shadow mb-6">
                <h3 className="text-xl font-bold mb-4">Neighborhood Stats</h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm text-gray-600">Median Household Income</dt>
                    <dd className="font-semibold">${stats.demographics.medianIncome.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-600">Average Home Age</dt>
                    <dd className="font-semibold">{stats.demographics.avgHomeAge} years</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-600">Owner Occupied</dt>
                    <dd className="font-semibold">{stats.demographics.ownerOccupied}%</dd>
                  </div>
                </dl>
              </div>

              {/* Local Amenities */}
              <div className="bg-white rounded-lg p-6 shadow mb-6">
                <h3 className="text-xl font-bold mb-4">Local Amenities</h3>
                <ul className="space-y-2">
                  {stats.amenities.map((amenity, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {amenity}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Related Searches */}
              <div className="bg-gray-100 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Related Searches</h3>
                <div className="space-y-2">
                  <Link to={`/search?subdivision=${neighborhoodName}&propertyType=condo`} className="block text-blue-600 hover:text-blue-800">
                    {neighborhoodName} Condos
                  </Link>
                  <Link to={`/search?subdivision=${neighborhoodName}&propertyAge=new`} className="block text-blue-600 hover:text-blue-800">
                    New Homes in {neighborhoodName}
                  </Link>
                  <Link to={`/search?subdivision=${neighborhoodName}&priceRange=luxury`} className="block text-blue-600 hover:text-blue-800">
                    Luxury Homes in {neighborhoodName}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};